// @ts-ignore
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Papa from "papaparse";
import { ScheduleEventApi } from "api/scheduleEvents/scheduleEventsApi";
import { TopicApi } from "api/topics/topicsApi";
import { formatTime } from "utils";

export const SCHEDULE_EXPORT_COLUMNS = [
  { key: "type", label: "Typ" },
  { key: "name", label: "Nazwa" },
  { key: "date", label: "Data" },
  { key: "timeFrom", label: "Czas od" },
  { key: "timeTo", label: "Czas do" },
  { key: "session", label: "Zjazd" },
  { key: "place", label: "Miejsce" },
  { key: "trainers", label: "Prowadzący" },
  { key: "groupTopics", label: "Tematy w grupie" },
] as const;

export type ScheduleExportColumnKey = (typeof SCHEDULE_EXPORT_COLUMNS)[number]["key"];

export interface ExportTable {
  headers: string[];
  rows: string[][];
}

export interface ExportOptions {
  includeTopics: boolean;
  includeBreaks: boolean;
  includeExams: boolean;
  includeTopicGroups: boolean;
  columns: ScheduleExportColumnKey[];
}

function getEventType(event: ScheduleEventApi): string {
  if (event.topicGroupName) return "Grupa tematów";
  if (event.topicName) return "Temat";
  if (event.examName) return "Egzamin";
  return "Przerwa";
}

function getEventName(event: ScheduleEventApi): string {
  return event.topicGroupName || event.topicName || event.examName || "Przerwa";
}

function formatDate(date: Date): string {
  const d = new Date(date);
  return d.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatTrainers(event: ScheduleEventApi): string {
  if (!event.trainersPrices || event.trainersPrices.length === 0) return "";
  return event.trainersPrices
    .map((t) => `${t.trainerFirstName} ${t.trainerSurname}`)
    .join(", ");
}

function shouldIncludeEvent(event: ScheduleEventApi, options: ExportOptions): boolean {
  const type = getEventType(event);
  switch (type) {
    case "Temat":
      return options.includeTopics;
    case "Przerwa":
      return options.includeBreaks;
    case "Egzamin":
      return options.includeExams;
    case "Grupa tematów":
      return options.includeTopicGroups;
    default:
      return false;
  }
}

function getColumnValue(
  key: ScheduleExportColumnKey,
  event: ScheduleEventApi,
  topicGroupDetailsMap?: Map<number, TopicApi[]>,
): string {
  switch (key) {
    case "type":
      return getEventType(event);
    case "name":
      return getEventName(event);
    case "date":
      return formatDate(event.dateFrom);
    case "timeFrom":
      return event.timeFrom;
    case "timeTo":
      return event.timeTo;
    case "session":
      return event.session?.name || "";
    case "place":
      return event.placeName || "";
    case "trainers":
      return formatTrainers(event);
    case "groupTopics":
      // One cell per group (newline-separated) instead of extra rows that repeated the group's date/place.
      if (!event.topicGroupId) return "";
      return (topicGroupDetailsMap?.get(event.topicGroupId) ?? []).map((topic) => topic.title).join("\n");
  }
}

export function mapEventsToExportTable(
  events: ScheduleEventApi[],
  options: ExportOptions,
  topicGroupDetailsMap?: Map<number, TopicApi[]>,
): ExportTable {
  const columns = SCHEDULE_EXPORT_COLUMNS.filter((column) => options.columns.includes(column.key));

  // Same chronological order as the schedule list on screen.
  const sortedEvents = [...events].sort((a, b) => new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime());

  return {
    headers: columns.map((column) => column.label),
    rows: sortedEvents
      .filter((event) => shouldIncludeEvent(event, options))
      .map((event) => columns.map((column) => getColumnValue(column.key, event, topicGroupDetailsMap))),
  };
}

export function exportScheduleCSV({ headers, rows }: ExportTable, fileName: string): void {
  const csv = Papa.unparse({ fields: headers, data: rows });
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  saveAs(blob, `${fileName}.csv`);
}

export function exportSchedulePDF({ headers, rows }: ExportTable, fileName: string, courseName?: string): void {
  const pdf = new jsPDF("landscape", "pt", "a4");
  pdf.setFont("Lato-Regular", "normal");

  const today = new Date();
  const exportedDate = today.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  pdf.setFontSize(14);
  pdf.text(`Harmonogram kursu${courseName ? ` „${courseName}”` : ""} — Eksportowano: ${exportedDate}`, 40, 30);

  autoTable(pdf, {
    head: [headers],
    body: rows,
    startY: 50,
    styles: {
      font: "Lato-Regular",
      fontStyle: "normal",
      fontSize: 8,
    },
    headStyles: {
      font: "Lato-Regular",
      fontStyle: "normal",
      fontSize: 10,
      fillColor: [22, 160, 133],
      halign: "center",
    },
    margin: { horizontal: 10 },
    tableWidth: "auto",
    useCss: true,
  });

  pdf.save(`${fileName}.pdf`);
}
